import React, { FC, ComponentProps } from 'react';
import styles from './styles.module.css';
import {
  TextInput,
  SelectInput,
  Radio,
  MultiSelectInput,
  ComboBox,
  RadioButtonGroup,
  RadioGroup,
  SelectAutocomplete,
  AddressAutocomplete,
  TextArea,
  LockedTextInput,
  SearchInput,
  ReadOnly,
} from './Variants';
import { Asset, WarningIcon } from '@clearcover/atlas';
import { isPresent } from 'Shared/utils/object';
import classnames from 'classnames';

/*
  1. Add new inputs here, and follow structure below.
*/
type VariantPropMapping =
  | ({
      variant: 'text';
    } & ComponentProps<typeof TextInput>)
  | ({
      variant: 'search';
    } & ComponentProps<typeof SearchInput>)
  | ({
      variant: 'textarea';
    } & ComponentProps<typeof TextArea>)
  | ({
      variant: 'select';
    } & ComponentProps<typeof SelectInput>)
  | ({
      variant: 'radio';
    } & ComponentProps<typeof Radio>)
  | ({
      variant: 'radioGroup';
    } & ComponentProps<typeof RadioGroup>)
  | ({
      variant: 'multiselect';
    } & ComponentProps<typeof MultiSelectInput>)
  | ({
      variant: 'combobox';
    } & ComponentProps<typeof ComboBox>)
  | ({
      variant: 'radioButtonGroup';
    } & ComponentProps<typeof RadioButtonGroup>)
  | ({
      variant: 'selectAutocomplete';
    } & ComponentProps<typeof SelectAutocomplete>)
  | ({
      variant: 'addressAutocomplete';
    } & ComponentProps<typeof AddressAutocomplete>)
  | ({
      variant: 'lockedText';
    } & ComponentProps<typeof LockedTextInput>)
  | ({
      variant: 'readOnly';
    } & ComponentProps<typeof ReadOnly>);

export type FormInputProps = {
  errorMessage?: string;
  renderErrorMessage?: boolean;
  loading?: boolean;
} & VariantPropMapping;

export const FormInput: FC<FormInputProps> = (props) => {
  const { renderErrorMessage, ...inputProps } = props;

  return (
    <div className={styles.wrapper}>
      {(() => {
        // 2. Add new input as shown below.
        // The switch is required to refine the type of inputProps

        switch (inputProps.variant) {
          case 'text':
            return <TextInput {...inputProps} />;
          case 'search':
            return <SearchInput {...inputProps} />;
          case 'textarea':
            return <TextArea {...inputProps} />;
          case 'select':
            return <SelectInput {...inputProps} />;
          case 'radio':
            return <Radio {...inputProps} />;
          case 'radioGroup':
            return <RadioGroup {...inputProps} />;
          case 'multiselect':
            return <MultiSelectInput {...inputProps} />;
          case 'combobox':
            return <ComboBox {...inputProps} />;
          case 'radioButtonGroup':
            return <RadioButtonGroup {...inputProps} />;
          case 'selectAutocomplete':
            return <SelectAutocomplete {...inputProps} />;
          case 'addressAutocomplete':
            return <AddressAutocomplete {...inputProps} />;
          case 'lockedText':
            return <LockedTextInput {...inputProps} />;
          case 'readOnly':
            return <ReadOnly {...inputProps} />;
          default:
            return null;
        }
      })()}
      {isPresent(props.errorMessage) &&
        props.variant !== 'readOnly' &&
        renderErrorMessage !== false && (
          <FormInputErrorMessage {...props} message={props.errorMessage} />
        )}
    </div>
  );
};

export const FormInputErrorMessage = ({
  message,
  className,
  name,
}: {
  message: string;
  className?: string;
  name?: string;
}) => (
  <div
    data-testid={`error-message-for-${name}`}
    className={classnames(styles.error, className)}
  >
    <Asset icon={WarningIcon} height={14} width={14} description="error icon" />
    <div className={styles.errorMessage}>{message}</div>
  </div>
);
